import { Tag } from '@dev-spendesk/grapes';
import { useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';

import { useCanAccessCompanySettings } from 'modules/company/general-settings/hooks';
import { HeaderWithNavigation } from 'src/core/common/components/HeaderWithNavigation/HeaderWithNavigation';
import { type NavigationLinkProps } from 'src/core/common/components/NavigationLink';
import { ProtectedRoute } from 'src/core/common/components/ProtectedRoute';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompany } from 'src/core/modules/app/hooks/useCompany';
import { ApprovalByDimensionsPage } from 'src/core/modules/approval-by-dimensions';
import { BankInformationPage } from 'src/core/modules/company/general-settings/components/BankInformationPage/BankInformationPage';
import { CardLimits } from 'src/core/modules/company/general-settings/components/CardLimits';
import { InstantPaymentLimits } from 'src/core/modules/company/general-settings/components/InstantPaymentLimits';
import { NotificationSettingsContainer } from 'src/core/modules/company/general-settings/components/NotificationSettings';
import { SpendingMethods } from 'src/core/modules/company/general-settings/components/SpendingMethods';
import { useShouldDisplayBankInformation } from 'src/core/modules/company/general-settings/hooks/useShouldDisplayBankInformation';
import { MembersApprovalPoliciesContainer } from 'src/core/modules/members/containers/MembersApprovalPoliciesContainer';
import { MembersControlRulesTabContainer } from 'src/core/modules/members/containers/MembersControlRulesTabContainer/MembersControlRulesTabContainer';
import { SpendLimitsPage } from 'src/core/modules/spend-limits';
import { getIsRequestCardLimitsIncreaseEnabled } from 'src/core/selectors/globalSelectors';

export const SettingsCompanyRulesPage = () => {
  const { t } = useTranslation('global');
  const hasControlOnSpendingMethods = useFeature(
    FEATURES.CONTROL_ON_SPENDING_METHODS,
  );
  const isSpendLimitsFeatureEnabled = useFeature(FEATURES.SPEND_LIMITS);
  const hasPlayByRulesFeature = useFeature(FEATURES.PLAY_BY_RULES);
  const company = useCompany();
  const canAccessCompanySettings = useCanAccessCompanySettings();
  const shouldDisplayBankInformation = useShouldDisplayBankInformation();
  const isApprovalByDimensionsEnabled = useFeature(
    FEATURES.MULTIDIMENSIONAL_APPROVAL_WORKFLOW,
  );
  // const hasTravelBudgetRequestsFeature = useFeature(
  //   FEATURES.TRAVEL_BUDGET_REQUEST,
  // );
  const isInstantPaymentLimitsEnabled = useFeature(
    FEATURES.TMP_INSTANT_PAYMENT,
  );

  const canAccessNotifications = canAccessCompanySettings;
  const canAccessSpendingMethods =
    hasControlOnSpendingMethods && canAccessCompanySettings;
  const canAccessBankInformation =
    shouldDisplayBankInformation && canAccessCompanySettings;
  const canAccessSpendLimits =
    isSpendLimitsFeatureEnabled && canAccessCompanySettings;
  const canAccessControlRules =
    canAccessCompanySettings && hasPlayByRulesFeature;
  const canAccessMembers = canAccessCompanySettings;
  // const canAccessTravelPolicies =
  //   canAccessCompanySettings && hasTravelBudgetRequestsFeature;

  const isRequestCardLimitsIncreaseEnabled = useSelector(
    getIsRequestCardLimitsIncreaseEnabled,
  );

  const links = [
    canAccessMembers
      ? {
          key: 'company-policies',
          text: t('members.navLinks.policies'),
          path: routeFor(routes.COMPANY_POLICIES.path, {
            company: company.id,
          }),
        }
      : null,
    isApprovalByDimensionsEnabled
      ? {
          key: 'approval-workflows',
          text: t('members.navLinks.approvalWorkflows'),
          path: routeFor(routes.APPROVAL_WORKFLOWS.path, {
            company: company.id,
          }),
        }
      : null,
    canAccessSpendLimits
      ? {
          key: 'spend-limits',
          text: t('settings.links.spendLimits'),
          id: 'spend-limits-link',
          path: routeFor(routes.SPEND_LIMITS.path, {
            company: company.id,
          }),
        }
      : null,
    // canAccessTravelPolicies
    //   ? {
    //       key: 'company-travel-policies',
    //       text: t('settings.links.travelPolicies'),
    //       path: routeFor(routes.TRAVEL_POLICIES.path, {
    //         company: company.id,
    //       }),
    //     }
    //   : null,
    canAccessControlRules
      ? {
          key: 'company-control-rules',
          text: t('members.navLinks.controlRules'),
          path: routeFor(routes.COMPANY_CONTROL_RULES.path, {
            company: company.id,
          }),
        }
      : null,
    canAccessSpendingMethods
      ? {
          key: 'company-spending-methods',
          text: t('generalSettings.navLinks.paymentMethods'),
          path: routeFor(routes.COMPANY_GENERAL_SETTINGS_PAYMENT_METHODS.path, {
            company: company.id,
          }),
          rightAddon: isRequestCardLimitsIncreaseEnabled ? (
            <Tag className="h-[20px]" variant="pink">
              {t('misc.new')}
            </Tag>
          ) : null,
        }
      : null,
    canAccessNotifications
      ? {
          key: 'company-notifications',
          text: t('generalSettings.navLinks.notifications'),
          path: routeFor(routes.COMPANY_GENERAL_SETTINGS_NOTIFICATIONS.path, {
            company: company.id,
          }),
        }
      : null,
  ].filter((item) => item !== null) as NavigationLinkProps[];

  return (
    <div className="page__container">
      <HeaderWithNavigation links={links}>
        {t('navigation.companyRules')}
      </HeaderWithNavigation>
      <Switch>
        <div className="bg-background-primary-default flex grow justify-center pt-0">
          <div className="flex w-full flex-col gap-24 pb-24">
            <Route exact path={routes.COMPANY_POLICIES.path}>
              <MembersApprovalPoliciesContainer />
            </Route>

            <Route exact path={routes.COMPANY_CONTROL_RULES.path}>
              <MembersControlRulesTabContainer />
            </Route>

            <ProtectedRoute
              path={[
                routes.COMPANY_GENERAL_SETTINGS_PAYMENT_METHODS.path,
                routes.COMPANY_GENERAL_SETTINGS_BANK_INFORMATION.path,
              ]}
              isAccessAllowed={canAccessSpendingMethods}
            >
              <div className="mx-auto flex max-w-lg flex-col gap-32">
                <SpendingMethods />
                {isInstantPaymentLimitsEnabled && <InstantPaymentLimits />}
                {canAccessBankInformation && <BankInformationPage />}
                <CardLimits />
              </div>
            </ProtectedRoute>

            <ProtectedRoute
              isAccessAllowed={canAccessNotifications}
              path={routes.COMPANY_GENERAL_SETTINGS_NOTIFICATIONS.path}
            >
              <NotificationSettingsContainer />
            </ProtectedRoute>

            <ProtectedRoute
              path={routes.SPEND_LIMITS.path}
              isAccessAllowed={isSpendLimitsFeatureEnabled}
            >
              <SpendLimitsPage />
            </ProtectedRoute>

            {/* <ProtectedRoute
              path={routes.TRAVEL_POLICIES.path}
              isAccessAllowed={canAccessTravelPolicies}
            >
              <TravelPoliciesPage />
            </ProtectedRoute> */}

            <ProtectedRoute
              path={routes.APPROVAL_WORKFLOWS.path}
              isAccessAllowed={isApprovalByDimensionsEnabled}
            >
              <ApprovalByDimensionsPage />
            </ProtectedRoute>
          </div>
        </div>
      </Switch>
    </div>
  );
};
